import { BaseSDK, SDKConfig } from "./base";
import { SDKName } from "./types";

// 简化：回调结果类型
export interface CallbackResult {
  data?: Record<string, unknown>;
  success?: boolean;
  error?: string;
}

declare global {
  interface Window {
    jsBridge?: {
      postMessage: (cmd: string, params: unknown) => void;
    };
    webkit?: {
      messageHandlers?: {
        jsBridge?: {
          postMessage: (message: string) => void;
        };
        executeCommand?: {
          postMessage: (message: Record<string, unknown>) => void;
        };
      };
    };
    jsbridge?: {
      hideSplashView?: () => void;
      executeCommand?: (command: string) => void;
      openAppBrowser?: (url: string) => void;
      postMessage?: (message: string) => void;
      getReportSdkData?: () => unknown;
    };
    onCommandResult?: (result: CallbackResult | unknown) => void;
    // iOS WebView 注入的 SDK 数据缓存
    __getReportSdkDataCache?: Record<string, unknown> | null;
  }
}

export class WebViewSDK extends BaseSDK {
  private pendingRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (reason: unknown) => void;
      timeout: NodeJS.Timeout;
      command: string;
    }
  >();

  constructor(config: SDKConfig) {
    super(SDKName.WebViewSDK, config);
  }

  async initialize(): Promise<void> {
    this.hideSplashScreen();
  }

  // 处理回调的方法
  private handleCommandResult(result: unknown): void {
    // 按先进先出处理第一个待处理的请求
    if (this.pendingRequests.size > 0) {
      const entries = this.pendingRequests.entries();
      const firstEntry = entries.next();
      if (!firstEntry.done) {
        const [firstRequestId, request] = firstEntry.value;
        clearTimeout(request.timeout);
        this.pendingRequests.delete(firstRequestId);
        request.resolve(result);
      }
    }
  }

  // 设置回调监听器
  private setupCallbackListener(): void {
    // 确保只设置一次监听器
    if (window.onCommandResult) {
      return;
    }

    window.onCommandResult = (result: CallbackResult | unknown) => {
      console.log("Received Native callback:", result);

      try {
        // 处理结果格式
        if (typeof result === "object" && result !== null) {
          const resultObj = result as CallbackResult;

          // 检查是否有错误
          if (resultObj.success === false || resultObj.error) {
            console.error("Native returned error:", resultObj.error);
            this.handleCommandResult({
              success: false,
              error: resultObj.error || "Unknown error",
            });
            return;
          }

          // 处理数据
          this.handleCommandResult(resultObj.data || resultObj);
        } else {
          // 兼容简单结果格式
          this.handleCommandResult(result);
        }
      } catch (error) {
        console.error("Error processing callback result:", error);
        this.handleCommandResult({
          success: false,
          error: "Failed to parse callback result",
        });
      }
    };
  }

  // 生成请求ID
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 清理过期的请求
  private cleanupExpiredRequests(): void {
    const now = Date.now();
    for (const [requestId, request] of this.pendingRequests.entries()) {
      // 清理超过30秒的请求
      const timestamp = parseInt(requestId.split("_")[1]);
      if (now - timestamp > 10000) {
        clearTimeout(request.timeout);
        this.pendingRequests.delete(requestId);
        request.reject(new Error("Request timeout"));
      }
    }
  }

  // 查找相同命令的请求
  private findSameCommandRequest(command: string): string | null {
    for (const [requestId, request] of this.pendingRequests.entries()) {
      if (request.command === command) {
        return requestId;
      }
    }
    return null;
  }

  // 清理相同命令的请求
  private clearSameCommandRequest(command: string): void {
    const sameRequestId = this.findSameCommandRequest(command);
    if (sameRequestId) {
      const request = this.pendingRequests.get(sameRequestId);
      if (request) {
        clearTimeout(request.timeout);
        this.pendingRequests.delete(sameRequestId);
        request.reject(
          new Error(`Same command ${command} was overridden by new request`),
        );
      }
    }
  }

  // 同步方法
  public hideSplashScreen(): void {
    console.log("hideSplashScreen");
    if (window.webkit?.messageHandlers?.jsBridge) {
      // iOS
      window.webkit.messageHandlers.jsBridge.postMessage("hideSplashView");
    } else if (window.jsbridge?.hideSplashView) {
      console.log("hideSplashView jsBridge");
      // Android
      window.jsbridge.hideSplashView();
    } else {
      console.log("hideSplashScreen not supported");
    }
  }

  public openAppBrowser(url: string): void {
    if (window.webkit?.messageHandlers?.jsBridge) {
      // iOS
      window.webkit.messageHandlers.jsBridge.postMessage("openAppBrowser");
    } else if (window.jsbridge?.openAppBrowser) {
      // Android
      window.jsbridge.openAppBrowser(url);
    }
  }

  public getReportSdkData(): Record<string, unknown> | unknown | null {
    console.error("getReportSdkData--publish--webView1--getReportSdkData");
    if (window.webkit?.messageHandlers?.jsBridge) {
      // iOS - 直接从 Native 注入的缓存中读取数据（同步）
      console.error("getReportSdkData--publish--webView2--getReportSdkData");
      console.error(
        "getReportSdkData--缓存状态",
        window.__getReportSdkDataCache,
      );

      // 检查缓存是否存在
      if (
        window.__getReportSdkDataCache !== undefined &&
        window.__getReportSdkDataCache !== null
      ) {
        console.error(
          "getReportSdkData--publish--webView2--从缓存获取数据",
          window.__getReportSdkDataCache,
        );
        return window.__getReportSdkDataCache;
      }

      // 缓存不存在：说明页面还没完全加载或 Native 注入失败
      // Native 会在页面加载时自动注入，这里不需要手动调用
      console.warn("getReportSdkData--缓存为空，Native尚未注入数据，返回null");
      return null;
    } else if (window.jsbridge?.getReportSdkData) {
      // Android
      return window.jsbridge.getReportSdkData();
    }
    return null;
  }

  // 异步方法
  public async executeCommand(
    command: string,
    params?: Record<string, unknown>,
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      // 设置回调监听器
      this.setupCallbackListener();

      // 清理过期请求
      this.cleanupExpiredRequests();

      // 清理相同命令的请求
      this.clearSameCommandRequest(command);

      // 生成请求ID
      const requestId = this.generateRequestId();

      // 设置超时
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Command execution timeout: ${command}`));
      }, 10000); // 10秒超时

      // 存储请求信息
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout,
        command,
      });

      try {
        if (window.webkit?.messageHandlers?.executeCommand) {
          // iOS
          window.webkit.messageHandlers.executeCommand.postMessage({
            command,
            params,
            callback: (result: unknown) => {
              this.handleCommandResult(result);
            },
          });
        } else if (window.jsbridge?.executeCommand) {
          // Android
          const commandData = {
            command,
            params,
          };
          window.jsbridge.executeCommand(JSON.stringify(commandData));
        } else {
          this.pendingRequests.delete(requestId);
          clearTimeout(timeout);
          reject(new Error("WebView environment not supported"));
        }
      } catch (error) {
        this.pendingRequests.delete(requestId);
        clearTimeout(timeout);
        reject(error);
      }
    });
  }

  _excuteIosFunc(funcName: string, params = "") {
    if (!prompt) {
      return;
    }
    if (!funcName) {
      return;
    }
    if (params == null || params == undefined) {
      params = "";
    }
    const type = "JSbridge";
    const payload = { type: type, funcName: funcName, params: params };
    const res = prompt(JSON.stringify(payload));
    return "ios-" + res;
  }

  reportEvent(event: string, data: Record<string, unknown>): void {
    return;
    // 处理事件上报
  }

  // 获取当前待处理的请求数量
  public getPendingRequestsCount(): number {
    return this.pendingRequests.size;
  }

  // 清理所有待处理的请求
  public clearAllPendingRequests(): void {
    for (const [requestId, request] of this.pendingRequests.entries()) {
      clearTimeout(request.timeout);
      request.reject(new Error("Request cancelled"));
    }
    this.pendingRequests.clear();
  }

  // 检查是否有待处理的请求
  public hasPendingRequests(): boolean {
    return this.pendingRequests.size > 0;
  }

  // 获取待处理请求的详细信息
  public getPendingRequestsInfo(): Array<{
    requestId: string;
    command: string;
    timestamp: number;
  }> {
    const info: Array<{
      requestId: string;
      command: string;
      timestamp: number;
    }> = [];

    for (const [requestId, request] of this.pendingRequests.entries()) {
      const parts = requestId.split("_");
      if (parts.length >= 2) {
        info.push({
          requestId,
          command: request.command,
          timestamp: parseInt(parts[1]) || 0,
        });
      }
    }

    return info;
  }

  // 手动触发回调（用于测试）
  public triggerCallback(result: unknown): void {
    this.handleCommandResult(result);
  }
}
